import { FC, createContext, useEffect, useContext, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import checkExpiredStatus from 'shared/checkExpiredStatus';
import CorporateOverview from "./CorporateOverview";
import CorporateDetail from "./CorporateDetail";
import useTruncated from "hooks/Truncated";
import { SessionTimeoutMsg } from 'App';
import { API, graphqlOperation } from 'aws-amplify';
import { getCorporate } from 'graphql/queries';

type CorporateTableDataProps = {
  data: string | number | null;
  className: string;
  colSpan?: number
}
type TruncatableTextProps = {
  text: string | number | null;
}
// 企業情報Context
export const CorporateInfo = createContext<CorporateInfo | undefined>({} as CorporateInfo);

/* ------------------------- 
  components
----------------------------*/
export const CorporateTableData: FC<CorporateTableDataProps> = ({ data, className, colSpan = 1 }) => {
  // 未購入項目グレーアウト
  const nutPurchased = data === null ? "bg-not-purchased" : "";
  return (
    <td className={`${className} ${nutPurchased} whitespace-nowrap`} colSpan={colSpan}>
      <TruncatableText text={data} />
    </td>
  )
}
export const TruncatableText: FC<TruncatableTextProps> = ({ text }) => {
  if (text === null) {
    text = "";
  }
  const [ref, truncated] = useTruncated(text);
  let tooltipStyle = "";
  let truncateStyle = "";
  let dataTip = "";
  if (truncated) {
    tooltipStyle = "tooltip tooltip-top block whitespace-normal";
    truncateStyle = "truncate";
    dataTip = text.toString();
  }
  return (
    <>
      <div className={`${tooltipStyle}`} data-tip={dataTip} ref={ref}>
        <p className={`${truncateStyle}`}>{text}</p>
      </div>
    </>
  )
}
const Corporate: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  /** 企業情報 */
  const [corporateInfo, setCorporateInfo] = useState<CorporateInfo>();
  /** ファイル名 */
  const [filename, setFilename] = useState<string>("");
  // セッションタイムアウトメッセージ
  const { hidden, setHidden } = useContext(SessionTimeoutMsg);
  /** 納品ID */
  const id = searchParams.get("id");
  /** アップロードID */
  const uploadId = searchParams.get("uploadId");
  /** 商品名 */
  const productName = searchParams.get("productName");
  /** 納品日付 */
  const deliveredDate = searchParams.get("deliveredDate");
  /** 有効期限日 */
  const expireDate = searchParams.get("expireDate");
  /** 企業ID */
  const corporateId = location.pathname.match(/^.+\/(.+?)\/*$/);
  /** 企業情報初期化 */
  useEffect(() => {
    setHidden('hidden');
    if (!(id && uploadId && productName && deliveredDate && expireDate && corporateId) || checkExpiredStatus(expireDate)) {
      navigate('/error');
      return;
    }
    const getCorporateInfo = async () => {
      let response;
      let extractedResData: CorporateInfo;
      try {
        response = await API.graphql(graphqlOperation(getCorporate, { id: decodeURIComponent(corporateId[1]) }));
        // @ts-ignore
        // 画面表示順に並べる
        const resData = response.data.getCorporate;
        extractedResData = {
          id: resData.id, // 企業ID
          uploadId: resData.uploadId, // アップロードID
          filename: resData.filename, // ファイル名
          RK_SZKN: resData.RK_SZKN, // 略式法人格付漢字商号
          CHS_NNGP: resData.CHS_NNGP, // 調査年月日
          R_CS_YMD: resData.R_CS_YMD, // レポート調査年月日
          KDB_UP_YMD: resData.KDB_UP_YMD, // 企業DB更新年月日
          KCD: resData.KCD, // TSR企業コード
          DUNS: resData.DUNS, // D-U-N-S(R) Number
          HJNO: resData.HJNO, // 法人番号
          KKCD: resData.KKCD, // 顧客コード
          TKSK_HKOJ_INNO: resData.TKSK_HKOJ_INNO, // 適格請求書発行事業者登録番号
          HYTN: resData.HYTN, // 評点
          RSK_SCR: resData.RSK_SCR, // リスクスコア
          SISK_SZKN: resData.SISK_SZKN, // 正式法人格付漢字商号
          HJTK_SGKT: resData.HJTK_SGKT, // 法人格付カナ商号
          SGEI: resData.SGEI, // 英文商号
          JST_SZIC_ZIP: resData.JST_SZIC_ZIP, // 企業郵便番号
          JST_ADKN: resData.JST_ADKN, // 企業所在地
          JST_ADEI: resData.JST_ADEI, // 英文企業所在地
          JST_SZIC_ZNTI_TEL: resData.JST_SZIC_ZNTI_TEL, // 企業電話番号
          KGY_URL: resData.KGY_URL, // 企業URL
          SHNKN: resData.SHNKN, // 資本金（千円）
          JJ_KBN: resData.JJ_KBN, // 上場市場区分
          KBCD: resData.KBCD, // 証券株式コード
          EDCD: resData.EDCD, // EDINETコード
          GKTK_SIJO_MI: resData.GKTK_SIJO_MI, // 議決権最上位者名
          JGYINS: resData.JGYINS, // 従業員数
          JGYSH_S: resData.JGYSH_S, // 事業所数
          KJYS: resData.KJYS, // 工場数
          SGY_NNGT: resData.SGY_NNGT, // 創業年月（西暦）
          STRT_YMD: resData.STRT_YMD, // 設立年月日（西暦）
          SYRK_GKNY: resData.SYRK_GKNY, // 概況
          GSCD_1: resData.GSCD_1, // 業種コード1
          GYSH_FBMI_1: resData.GYSH_FBMI_1, // 業種名称1
          GSCD_2: resData.GSCD_2, // 業種コード2
          GYSH_FBMI_2: resData.GYSH_FBMI_2, // 業種名称2
          GSCD_3: resData.GSCD_3, // 業種コード3
          GYSH_FBMI_3: resData.GYSH_FBMI_3, // 業種名称3
          GSCD_4: resData.GSCD_4, // 業種コード4
          GYSH_FBMI_4: resData.GYSH_FBMI_4, // 業種名称4
          GSCD_5: resData.GSCD_5, // 業種コード5
          GYSH_FBMI_5: resData.GYSH_FBMI_5, // 業種名称5
          GSCD_6: resData.GSCD_6, // 業種コード6
          GYSH_FBMI_6: resData.GYSH_FBMI_6, // 業種名称6
          AGCD_1: resData.AGCD_1, // 扱い品コード1
          ATKN_1: resData.ATKN_1, // 扱い品名称1
          AGCD_2: resData.AGCD_2, // 扱い品コード2
          ATKN_2: resData.ATKN_2, // 扱い品名称2
          AGCD_3: resData.AGCD_3, // 扱い品コード3
          ATKN_3: resData.ATKN_3, // 扱い品名称3
          AGCD_4: resData.AGCD_4, // 扱い品コード4
          ATKN_4: resData.ATKN_4, // 扱い品名称4
          AGCD_5: resData.AGCD_5, // 扱い品コード5
          ATKN_5: resData.ATKN_5, // 扱い品名称5
          AGCD_6: resData.AGCD_6, // 扱い品コード6
          ATKN_6: resData.ATKN_6, // 扱い品名称6
          JGNY_1: resData.JGNY_1, // 事業内容1
          JGNY_KSHR_1: resData.JGNY_KSHR_1, // 事業内容構成比率1
          JGNY_2: resData.JGNY_2, // 事業内容2
          JGNY_KSHR_2: resData.JGNY_KSHR_2, // 事業内容構成比率2
          JGNY_3: resData.JGNY_3, // 事業内容3
          JGNY_KSHR_3: resData.JGNY_KSHR_3, // 事業内容構成比率3
          JGNY_4: resData.JGNY_4, // 事業内容4
          JGNY_KSHR_4: resData.JGNY_KSHR_4, // 事業内容構成比率4
          JGNY_5: resData.JGNY_5, // 事業内容5
          JGNY_KSHR_5: resData.JGNY_KSHR_5, // 事業内容構成比率5
          JGNY_6: resData.JGNY_6, // 事業内容6
          JGNY_KSHR_6: resData.JGNY_KSHR_6, // 事業内容構成比率6
          GYSI_KSN_YM_1: resData.GYSI_KSN_YM_1, // 決算1_決算年月
          GYSI_KSN_JHKB_1: resData.GYSI_KSN_JHKB_1, // 決算1_見込・推定区分
          GYSI_MSU_1: resData.GYSI_MSU_1, // 決算1_月数
          SNTI_GYSI_URAG_1: resData.SNTI_GYSI_URAG_1, // 決算1_売上高（千円）
          GYSI_ZIKB_1: resData.GYSI_ZIKB_1, // 決算1_税込引区分
          SNTI_GYSI_REK_1: resData.SNTI_GYSI_REK_1, // 決算1_利益金（千円）
          GYSK_JSHR_1: resData.GYSK_JSHR_1, // 決算1_自己資本比率
          HISG_1: resData.HISG_1, // 決算1_配当総額（千円）
          ZUKB_1: resData.ZUKB_1, // 決算1_財務有無フラグ
          GYSI_KSN_YM_2: resData.GYSI_KSN_YM_2, // 決算2_決算年月
          GYSI_KSN_JHKB_2: resData.GYSI_KSN_JHKB_2, // 決算2_見込・推定区分
          GYSI_MSU_2: resData.GYSI_MSU_2, // 決算2_月数
          SNTI_GYSI_URAG_2: resData.SNTI_GYSI_URAG_2, // 決算2_売上高（千円）
          GYSI_ZIKB_2: resData.GYSI_ZIKB_2, // 決算2_税込引区分
          SNTI_GYSI_REK_2: resData.SNTI_GYSI_REK_2, // 決算2_利益金（千円）
          GYSK_JSHR_2: resData.GYSK_JSHR_2, // 決算2_自己資本比率
          HISG_2: resData.HISG_2, // 決算2_配当総額（千円）
          ZUKB_2: resData.ZUKB_2, // 決算2_財務有無フラグ
          GYSI_KSN_YM_3: resData.GYSI_KSN_YM_3, // 決算3_決算年月
          GYSI_KSN_JHKB_3: resData.GYSI_KSN_JHKB_3, // 決算3_見込・推定区分
          GYSI_MSU_3: resData.GYSI_MSU_3, // 決算3_月数
          SNTI_GYSI_URAG_3: resData.SNTI_GYSI_URAG_3, // 決算3_売上高（千円）
          GYSI_ZIKB_3: resData.GYSI_ZIKB_3, // 決算3_税込引区分
          SNTI_GYSI_REK_3: resData.SNTI_GYSI_REK_3, // 決算3_利益金（千円）
          GYSK_JSHR_3: resData.GYSK_JSHR_3, // 決算3_自己資本比率
          HISG_3: resData.HISG_3, // 決算3_配当総額（千円）
          ZUKB_3: resData.ZUKB_3, // 決算3_財務有無フラグ
          URJN_TSHO_KSN_YM: resData.URJN_TSHO_KSN_YM, // 業種内売上順位_決算年月
          ZNKK_URJN: resData.ZNKK_URJN, // 業種内売上順位_全国順位
          ZNKK_URJN_TSHO_SASU: resData.ZNKK_URJN_TSHO_SASU, // 業種内売上順位_全国対象社数
          KNBT_URJN: resData.KNBT_URJN, // 業種内売上順位_県別順位
          KNBT_URJN_TSHO_SASU: resData.KNBT_URJN_TSHO_SASU, // 業種内売上順位_県別対象社数
          DHS_SIMIKN: resData.DHS_SIMIKN, // 代表者氏名
          DHS_SIMIKT: resData.DHS_SIMIKT, // 代表者氏名カナ
          DHS_SIMIEI: resData.DHS_SIMIEI, // 英文代表者氏名
          DHS_ADD_ZIP: resData.DHS_ADD_ZIP, // 代表者郵便番号
          DHS_ZNTI_TEL: resData.DHS_ZNTI_TEL, // 代表者電話番号
          SEBT_KBN: resData.SEBT_KBN, // 代表者男女区分
          SNNGP_SRK: resData.SNNGP_SRK, // 代表者生年月日（西暦）
          DHS_ETMI: resData.DHS_ETMI, // 代表者干支名称
          DHS_ADKN: resData.DHS_ADKN, // 代表者現住所
          DHS_JSKT: resData.DHS_JSKT, // 代表者現住所カナ
          DHS_YSMI: resData.DHS_YSMI, // 代表者役名
          DHS_KGMI: resData.DHS_KGMI, // 代表者肩書名
          DHS_SHUN_YMD: resData.DHS_SHUN_YMD, // 代表者就任年月日（西暦）
          TSN_KRK_KBN: resData.TSN_KRK_KBN, // 代表者倒産経歴
          DHS_HCMI: resData.DHS_HCMI, // 代表者出身地名称
          DHS_SIS_EDU_GKMI: resData.DHS_SIS_EDU_GKMI, // 代表者最終学歴_学校名称
          DHS_GRKN: resData.DHS_GRKN, // 代表者最終学歴_卒業名
          DHS_JUMI: resData.DHS_JUMI, // 代表者住居名称
          DHS_HOMI_1: resData.DHS_HOMI_1, // 代表者趣味名称1
          DHS_HOMI_2: resData.DHS_HOMI_2, // 代表者趣味名称2
          DHS_HOMI_3: resData.DHS_HOMI_3, // 代表者趣味名称3
          YIMI_1: resData.YIMI_1, // 役員名1
          YSMI_1: resData.YSMI_1, // 役名1
          YKSH_KGMI_1: resData.YKSH_KGMI_1, // 肩書名1
          YIMI_2: resData.YIMI_2, // 役員名2
          YSMI_2: resData.YSMI_2, // 役名2
          YKSH_KGMI_2: resData.YKSH_KGMI_2, // 肩書名2
          YIMI_3: resData.YIMI_3, // 役員名3
          YSMI_3: resData.YSMI_3, // 役名3
          YKSH_KGMI_3: resData.YKSH_KGMI_3, // 肩書名3
          YIMI_4: resData.YIMI_4, // 役員名4
          YSMI_4: resData.YSMI_4, // 役名4
          YKSH_KGMI_4: resData.YKSH_KGMI_4, // 肩書名4
          YIMI_5: resData.YIMI_5, // 役員名5
          YSMI_5: resData.YSMI_5, // 役名5
          YKSH_KGMI_5: resData.YKSH_KGMI_5, // 肩書名5
          YIMI_6: resData.YIMI_6, // 役員名6
          YSMI_6: resData.YSMI_6, // 役名6
          YKSH_KGMI_6: resData.YKSH_KGMI_6, // 肩書名6
          YIMI_7: resData.YIMI_7, // 役員名7
          YSMI_7: resData.YSMI_7, // 役名7
          YKSH_KGMI_7: resData.YKSH_KGMI_7, // 肩書名7
          YIMI_8: resData.YIMI_8, // 役員名8
          YSMI_8: resData.YSMI_8, // 役名8
          YKSH_KGMI_8: resData.YKSH_KGMI_8, // 肩書名8
          YIMI_9: resData.YIMI_9, // 役員名9
          YSMI_9: resData.YSMI_9, // 役名9
          YKSH_KGMI_9: resData.YKSH_KGMI_9, // 肩書名9
          YIMI_10: resData.YIMI_10, // 役員名10
          YSMI_10: resData.YSMI_10, // 役名10
          YKSH_KGMI_10: resData.YKSH_KGMI_10, // 肩書名10
          KNMI_1: resData.KNMI_1, // 株主名称1
          CTPR_MCWR_1: resData.CTPR_MCWR_1, // 持株比率1
          KNMI_2: resData.KNMI_2, // 株主名称2
          CTPR_MCWR_2: resData.CTPR_MCWR_2, // 持株比率2
          KNMI_3: resData.KNMI_3, // 株主名称3
          CTPR_MCWR_3: resData.CTPR_MCWR_3, // 持株比率3
          KNMI_4: resData.KNMI_4, // 株主名称4
          CTPR_MCWR_4: resData.CTPR_MCWR_4, // 持株比率4
          KNMI_5: resData.KNMI_5, // 株主名称5
          CTPR_MCWR_5: resData.CTPR_MCWR_5, // 持株比率5
          KNMI_6: resData.KNMI_6, // 株主名称6
          CTPR_MCWR_6: resData.CTPR_MCWR_6, // 持株比率6
          FICD_1: resData.FICD_1, // 取引銀行コード1
          FIKN_1: resData.FIKN_1, // 取引銀行名1
          TNKN_1: resData.TNKN_1, // 取引銀行店舗名1
          FICD_2: resData.FICD_2, // 取引銀行コード2
          FIKN_2: resData.FIKN_2, // 取引銀行名2
          TNKN_2: resData.TNKN_2, // 取引銀行店舗名2
          FICD_3: resData.FICD_3, // 取引銀行コード3
          FIKN_3: resData.FIKN_3, // 取引銀行名3
          TNKN_3: resData.TNKN_3, // 取引銀行店舗名3
          FICD_4: resData.FICD_4, // 取引銀行コード4
          FIKN_4: resData.FIKN_4, // 取引銀行名4
          TNKN_4: resData.TNKN_4, // 取引銀行店舗名4
          FICD_5: resData.FICD_5, // 取引銀行コード5
          FIKN_5: resData.FIKN_5, // 取引銀行名5
          TNKN_5: resData.TNKN_5, // 取引銀行店舗名5
          FICD_6: resData.FICD_6, // 取引銀行コード6
          FIKN_6: resData.FIKN_6, // 取引銀行名6
          TNKN_6: resData.TNKN_6, // 取引銀行店舗名6
          FICD_7: resData.FICD_7, // 取引銀行コード7
          FIKN_7: resData.FIKN_7, // 取引銀行名7
          TNKN_7: resData.TNKN_7, // 取引銀行店舗名7
          FICD_8: resData.FICD_8, // 取引銀行コード8
          FIKN_8: resData.FIKN_8, // 取引銀行名8
          TNKN_8: resData.TNKN_8, // 取引銀行店舗名8
          FICD_9: resData.FICD_9, // 取引銀行コード9
          FIKN_9: resData.FIKN_9, // 取引銀行名9
          TNKN_9: resData.TNKN_9, // 取引銀行店舗名9
          FICD_10: resData.FICD_10, // 取引銀行コード10
          FIKN_10: resData.FIKN_10, // 取引銀行名10
          TNKN_10: resData.TNKN_10, // 取引銀行店舗名10
          SSKN_1: resData.SSKN_1, // 仕入先名称1
          SSKN_2: resData.SSKN_2, // 仕入先名称2
          SSKN_3: resData.SSKN_3, // 仕入先名称3
          SSKN_4: resData.SSKN_4, // 仕入先名称4
          SSKN_5: resData.SSKN_5, // 仕入先名称5
          SSKN_6: resData.SSKN_6, // 仕入先名称6
          HSKN_1: resData.HSKN_1, // 販売先名称1
          HSKN_2: resData.HSKN_2, // 販売先名称2
          HSKN_3: resData.HSKN_3, // 販売先名称3
          HSKN_4: resData.HSKN_4, // 販売先名称4
          HSKN_5: resData.HSKN_5, // 販売先名称5
          HSKN_6: resData.HSKN_6, // 販売先名称6
          SIR_CHKS_YSKB: resData.SIR_CHKS_YSKB, // 仕入先直接輸入区分
          HNBI_CHKS_YSKB: resData.HNBI_CHKS_YSKB, // 販売先直接輸出区分
          KKNI_OKIS_KCD: resData.KKNI_OKIS_KCD, // 国内親会社TSR企業コード
          KKNI_OKIS_SGKN: resData.KKNI_OKIS_SGKN, // 国内親会社漢字商号
          KKNI_SIJO_OKIS_KCD: resData.KKNI_SIJO_OKIS_KCD, // 国内最上位親会社TSR企業コード
          KKNI_SIJO_OKIS_SGKN: resData.KKNI_SIJO_OKIS_SGKN, // 国内最上位親会社漢字商号
          RS_SNSH_YMD_1: resData.RS_SNSH_YMD_1, // リスクスコア算出日
          ZN_AVG_RS: resData.ZN_AVG_RS, // 全体平均スコア
          TSHR: resData.TSHR, // 倒産確率
          GITO_MBCD: resData.GITO_MBCD, // リスクスコア業種コード
          GITO_MBMI: resData.GITO_MBMI, // リスクスコア業種名称
          RS_GSSU: resData.RS_GSSU, // リスクスコア業種総件数
          GYSH_AVG_RS: resData.GYSH_AVG_RS, // リスクスコア業種平均
          NRCD_1: resData.NRCD_1, // リーズンコード1
          NRCD_2: resData.NRCD_2, // リーズンコード2
          NRCD_3: resData.NRCD_3, // リーズンコード3
          NRCD_4: resData.NRCD_4, // リーズンコード4
          NRCD_5: resData.NRCD_5, // リーズンコード5
          URAG_YSSC: resData.URAG_YSSC, // 売上予測スコア
          URAG_YSSC_SNSH_YM: resData.URAG_YSSC_SNSH_YM, // 売上予測スコア算出月
          KYHG_SCORE: resData.KYHG_SCORE, // 休廃業予測スコア
          KYHG_SNSH_YM: resData.KYHG_SNSH_YM, // 休廃業予測スコア算出月
          KISN_SCORE: resData.KISN_SCORE, // 解散予測スコア
          KISN_SNSH_YM: resData.KISN_SNSH_YM, // 解散予測スコア算出月
          HGPI_SCORE: resData.HGPI_SCORE, // 被合併予測スコア
          HGPI_YSSC_SNSH_YM: resData.HGPI_YSSC_SNSH_YM, // 被合併予測スコア算出月
        }
        setCorporateInfo(extractedResData);
        setFilename(extractedResData.filename);
      } catch (e) {
        console.log(e)
        navigate('/error');
        return;
      }
    };
    getCorporateInfo();
  }, []);
  return (
    <>
      <div className="mt-8 mb-20 px-8 md:px-16 w-full lg:w-11/12">
        <CorporateInfo.Provider value={corporateInfo} >
          <div className="lg:flex mb-12 lg:mb-8">
            <p className="text-sm">{`${productName}（${deliveredDate}）> ${filename} > 企業データ詳細`}</p>
            <div className="flex flex-col md:flex-row mr-0 ml-auto gap-4 my-3 lg:my-0">
              <button
                tabIndex={-1}
                className="btn btn-sm w-[17rem] text-white flex-0 rounded-full border-none bg-gradient-to-b from-main to-main-to"
                onClick={() => navigate(`/index/?id=${id}&uploadId=${uploadId}&productName=${productName}&deliveredDate=${deliveredDate}&expireDate=${expireDate}`)}>
                <span className="material-symbols-outlined pr-1 md:pr-3 ">expand_circle_right</span>
                <span>商品ダウンロード画面に戻る</span>
              </button>
              <button
                tabIndex={-1}
                className="btn btn-sm w-[17rem] text-white flex-0 rounded-full border-none bg-gradient-to-b from-main to-main-to"
                onClick={() =>
                  navigate(
                    `/corporatelist/?id=${id}&uploadId=${uploadId}&productName=${productName}&deliveredDate=${deliveredDate}&expireDate=${expireDate}`,
                    { state: { ratingRange: null, riskRange: null, orderDate: location.state.orderDate } }
                  )
                }>
                <span className="material-symbols-outlined pr-1 md:pr-3 ">database</span>
                <span>企業データ一覧画面に戻る</span>
              </button>
            </div>
          </div>
          <div className='w-full'>
            <CorporateOverview />
          </div>
          <div className='w-full'>
            <CorporateDetail />
          </div>
        </CorporateInfo.Provider>
      </div>
    </>
  );
}

export default Corporate;
